import { useMemo, memo } from 'react'
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'

type EmotionData = {
  emotion: string
  confidence: number
}

type PieChartProps = {
  emotions: EmotionData[]
}

const COLORS = ['#ef4444', '#f59e0b', '#8b5cf6', '#22c55e', '#6b7280', '#3b82f6', '#ec4899']

function PieChartComponent({ emotions }: PieChartProps) {
  const data = useMemo(() => {
    return emotions.map((e) => ({
      name: e.emotion.charAt(0).toUpperCase() + e.emotion.slice(1),
      value: Number((e.confidence * 100).toFixed(1)),
    }))
  }, [emotions])

  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>No data available</div>
  }

  const totalValue = data.reduce((sum, item) => sum + item.value, 0)
  if (totalValue === 0) {
    return <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>All values are zero</div>
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RechartsPieChart margin={{ top: 0, right: 0, bottom: 40, left: 0 }}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => {
            if (!percent || percent < 0.05) return ''
            return `${name} ${(percent * 100).toFixed(0)}%`
          }}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
          isAnimationActive={true}
          animationDuration={600}
          animationBegin={0}
          animationEasing="ease-out"
        >
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number) => `${value}%`} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
        <Legend verticalAlign="bottom" height={36} />
      </RechartsPieChart>
    </ResponsiveContainer>
  )
}

export default memo(PieChartComponent, (prevProps, nextProps) => {
  if (prevProps.emotions.length !== nextProps.emotions.length) {
    return false
  }
  
  const prevMap = new Map(prevProps.emotions.map((e) => [e.emotion, e.confidence]))
  const nextMap = new Map(nextProps.emotions.map((e) => [e.emotion, e.confidence]))
  
  for (const emotion of prevProps.emotions) {
    const prevConf = prevMap.get(emotion.emotion) || 0
    const nextConf = nextMap.get(emotion.emotion) || 0
    if (Math.abs(prevConf - nextConf) > 0.001) {
      return false
    }
  }
  
  return true
})

